import { useCallback, useEffect, useRef, useState } from 'react'
import { useWeb3React } from '@web3-react/core'

export type API_PROFILE = {
  id?: number
  wallet?: string
  code?: string
  isActivated?: boolean
  isQualified?: boolean
  activationTxHash?: string | null
  activatedAt?: string | null
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

export type VerifyBurnTxRequest = {
  wallet: string
  burnTxHash: string
}

export type VerifyBurnTxResponse = {
  success?: boolean
  override?: unknown | null
  error?: {
    message?: string
  } | null
  [key: string]: unknown
}

export type CreateReferralCodeRequest = {
  wallet: string
  referralCode: string
}

export type CreateReferralCodeResponse = {
  success?: boolean
  override?: unknown | null
  error?: {
    message?: string
  } | null
  [key: string]: unknown
}

export type ReferralBindRequest = {
  wallet: string
  referralCode: string
}

export type GetOverviewResponse = {
  success?: boolean
  data?: {
    wallet?: string
    totalTradeVolume?: {
      toonWei?: string
      usdE18?: string
    } | null
    points?: {
      totalE18?: string
    } | null
    profile?: API_PROFILE | null
    claimableReferralRebateToonWei?: string
    paidToonWei?: string
    referralUsers?: {
      count?: number
      list?: Array<{
        id?: number
        wallet?: string
        parentWallet?: string
        sourceCode?: string
        boundAt?: string
        createdAt?: string
        updatedAt?: string
        [key: string]: unknown
      }>
      pagination?: {
        page?: number
        limit?: number
        totalPages?: number
      } | null
    } | null
  } | null
  [key: string]: unknown
}

export type OverviewData = NonNullable<GetOverviewResponse['data']>

const PROFILE_API_BASE = process.env.REACT_APP_API_BASE_URL || ''
const PROFILE_BURN_VERIFY_PATH = '/referral/burn/verify'
const PROFILE_CODE_CREATE_PATH = '/referral/code/create'
const PROFILE_BIND_PATH = '/referral/bind'
const PROFILE_CLAIM_PATH = '/referral/claim'
const PROFILE_GET_OVERVIEW_PATH = '/users/overview'
const PROFILE_MERKLE_CLAIM_PATH = '/referral/merkle/claim'
/** Response from GET /referral/merkle/claim?wallet=... */
export type MerkleClaimData = {
  claimable?: string
  totalReferralAllTimeWei?: string
  proof?: string[]
  root?: string
  claimedAmount?: string
  claimed?: boolean
  period?: string
  [key: string]: unknown
}

export type MerkleClaimResponse = {
  success?: boolean
  data?: MerkleClaimData | null
  [key: string]: unknown
}

const joinUrl = (base: string, path: string) => {
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${normalizedBase}${normalizedPath}`
}

const getVerifyBurnEndpoint = () => joinUrl(PROFILE_API_BASE, PROFILE_BURN_VERIFY_PATH)
const getCreateReferralCodeEndpoint = () => joinUrl(PROFILE_API_BASE, PROFILE_CODE_CREATE_PATH)
const getReferralBindEndpoint = () => joinUrl(PROFILE_API_BASE, PROFILE_BIND_PATH)
const getClaimRebateEndpoint = () => joinUrl(PROFILE_API_BASE, PROFILE_CLAIM_PATH)
const getOverviewEndpoint = (wallet: string) =>
  `${joinUrl(PROFILE_API_BASE, PROFILE_GET_OVERVIEW_PATH)}?wallet=${encodeURIComponent(wallet)}`
const getMerkleClaimEndpoint = (wallet: string) =>
  `${joinUrl(PROFILE_API_BASE, PROFILE_MERKLE_CLAIM_PATH)}?wallet=${encodeURIComponent(wallet)}`

export const postVerifyBurnTx = async (payload: VerifyBurnTxRequest): Promise<VerifyBurnTxResponse> => {
  if (!PROFILE_API_BASE) {
    throw new Error('Profile API is not configured.')
  }

  const response = await fetch(getVerifyBurnEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as VerifyBurnTxResponse
}

export const postCreateReferralCode = async (
  payload: CreateReferralCodeRequest,
): Promise<CreateReferralCodeResponse> => {
  if (!PROFILE_API_BASE) {
    throw new Error('Profile API is not configured.')
  }

  const response = await fetch(getCreateReferralCodeEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return (await response.json()) as CreateReferralCodeResponse
}

export const postReferralBind = async (
  payload: ReferralBindRequest,
): Promise<{ success?: boolean; error?: { message?: string } }> => {
  if (!PROFILE_API_BASE) {
    throw new Error('Profile API is not configured.')
  }

  const response = await fetch(getReferralBindEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data?.error?.message as string) || `HTTP ${response.status}`)
  }
  return data
}

export type ClaimRebateRequest = {
  wallet: string
}

export type ClaimRebateResponse = {
  override?: unknown | null
  error?: { message?: string }
  success?: boolean
  [key: string]: unknown
}

export const postClaimRebate = async (payload: ClaimRebateRequest): Promise<ClaimRebateResponse> => {
  if (!PROFILE_API_BASE) {
    throw new Error('Profile API is not configured.')
  }

  const response = await fetch(getClaimRebateEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const data = (await response.json().catch(() => ({}))) as ClaimRebateResponse
  if (!response.ok) {
    throw new Error((data?.error?.message as string) || `HTTP ${response.status}`)
  }
  return data
}

/**
 * Get the ref= query param from the current URL.
 * Checks both location.search (e.g. /?ref=CODE) and location.hash (e.g. #/?ref=CODE or #/swap?ref=CODE)
 * so it works regardless of router or server redirects.
 */
const REFERRAL_REF_STORAGE_KEY = 'toonswap_referral_ref'

// Same rule as creating a code: 4 to 32 letters, numbers, _ or -. A link carrying anything
// else cannot point to a code, so it is neither saved nor sent.
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{4,32}$/
const validReferralCode = (code?: string | null): string | null =>
  code && REFERRAL_CODE_PATTERN.test(code) ? code : null

export function getRefFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const fromSearch = validReferralCode(new URLSearchParams(window.location.search).get('ref')?.trim())
  if (fromSearch) return fromSearch
  const { hash } = window.location
  const hashQueryStart = hash.indexOf('?')
  if (hashQueryStart !== -1) {
    const fromHash = validReferralCode(new URLSearchParams(hash.slice(hashQueryStart)).get('ref')?.trim())
    if (fromHash) return fromHash
  }
  return null
}

function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return validReferralCode(localStorage.getItem(REFERRAL_REF_STORAGE_KEY))
  } catch {
    return null
  }
}

function setStoredReferralCode(code: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(REFERRAL_REF_STORAGE_KEY, code)
  } catch {
    // ignore
  }
}

function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(REFERRAL_REF_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Store ref from URL in localStorage when present; when user connects wallet,
 * POST /referral/bind with { wallet, referralCode } (ref from URL or localStorage). Runs once per account.
 */
export function useReferralBind(account: string | null | undefined): void {
  const boundAccountRef = useRef<string | null>(null)

  useEffect(() => {
    const refFromUrl = getRefFromUrl()
    if (refFromUrl) setStoredReferralCode(refFromUrl)
  }, [])

  useEffect(() => {
    if (!account) return
    const refFromUrl = getRefFromUrl()
    if (refFromUrl) setStoredReferralCode(refFromUrl)
    const ref = refFromUrl ?? getStoredReferralCode()
    if (!ref) return
    if (boundAccountRef.current === account) return
    postReferralBind({ wallet: account, referralCode: ref })
      .then(() => {
        boundAccountRef.current = account
        clearStoredReferralCode()
      })
      .catch(() => undefined)
  }, [account])
}

export const fetchOverview = async (wallet: string): Promise<GetOverviewResponse> => {
  if (!PROFILE_API_BASE) {
    throw new Error('Profile API is not configured.')
  }

  const response = await fetch(getOverviewEndpoint(wallet), {
    method: 'GET',
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return (await response.json()) as GetOverviewResponse
}

export const fetchReferralMerkleClaim = async (wallet: string): Promise<MerkleClaimResponse> => {
  if (!PROFILE_API_BASE) {
    throw new Error('Profile API is not configured.')
  }
  const response = await fetch(getMerkleClaimEndpoint(wallet), {
    method: 'GET',
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return (await response.json()) as MerkleClaimResponse
}

const useProfileApi = (account?: string | null) => {
  const { connector, chainId } = useWeb3React()
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null)
  const [isFetchingOverview, setIsFetchingOverview] = useState(false)
  const [overviewError, setOverviewError] = useState<string | null>(null)
  const [merkleClaimData, setMerkleClaimData] = useState<MerkleClaimData | null>(null)

  useEffect(() => {
    setOverviewData(null)
    setIsFetchingOverview(false)
    setOverviewError(null)
    setMerkleClaimData(null)
  }, [account])

  const fetchUserOverview = useCallback(async (): Promise<boolean> => {
    if (!account) {
      setOverviewData(null)
      return false
    }

    try {
      setIsFetchingOverview(true)
      setOverviewError(null)

      const body = await fetchOverview(account)
      const isSuccess = Boolean(body?.success)
      const data = body?.data ?? null

      setOverviewData(data)
      return isSuccess
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load overview data.'
      setOverviewError(message)
      setOverviewData(null)
      return false
    } finally {
      setIsFetchingOverview(false)
    }
  }, [account])

  const fetchMerkleClaim = useCallback(async (): Promise<boolean> => {
    if (!account) {
      setMerkleClaimData(null)
      return false
    }
    try {
      const body = await fetchReferralMerkleClaim(account)
      const data = body?.success ? body?.data ?? null : null
      setMerkleClaimData(data)
      return Boolean(body?.success)
    } catch {
      setMerkleClaimData(null)
      return false
    }
  }, [account])

  useEffect(() => {
    if (account) {
      fetchUserOverview()
      fetchMerkleClaim()
    }
  }, [account, fetchUserOverview, fetchMerkleClaim])

  return {
    connector,
    chainId,
    overviewData,
    merkleClaimData,
    isFetchingOverview,
    overviewError,
    fetchUserOverview,
    fetchMerkleClaim,
  }
}

export default useProfileApi
